/**
 * 屏 6 — 设置
 *
 * demo-gdd 附录 B 屏 6：
 *   - LLM 配置：API key 输入（demo 阶段存 localStorage）
 *   - 引擎调优：tick_rate / soft_timeout / hard_timeout（高级，默认隐藏）
 *   - Evals 评估：跑炉对战 + 代码质量分析（Harness 自动化层 FR-C）
 *   - 主题：dark（demo 锁 dark）
 *   - 关于：版本号 / 资源链接
 */

import { runEvals, saveEvalsReport, loadEvalsHistory } from "../services/evals-service";
import { getTheme, setTheme } from "../services/theme-service";
import { svgLoader, BATTLE_SVG_KEYS } from "../utils/svg-loader";
import { refreshColorTokens } from "../utils/color-tokens";
import { t, getLocale, setLocale } from "../i18n";
import type { Locale } from "../i18n/types";

export function renderScreen6Settings(root: HTMLElement, onBack: () => void) {
  const apiKey = localStorage.getItem("battlecode-llm-api-key") ?? "";
  const dailyCount = parseInt(localStorage.getItem("battlecode-llm-daily-count") ?? "0", 10);
  const dailyLimit = 100;
  // 末四位遮罩显示，避免泄漏
  const masked = apiKey.length > 8
    ? `${apiKey.slice(0, 4)}…${apiKey.slice(-4)}`
    : apiKey ? "（已配置）" : "（未配置）";

  const currentLocale = getLocale();
  root.innerHTML = `
    <div class="screen screen-6">
      <header class="screen-header">
        <button class="btn btn-ghost" id="btn-back">${t("settings.back_btn")}</button>
        <h1 class="logo">${t("settings.title")}</h1>
        <div></div>
      </header>

      <main class="screen-main settings-main">
        <section class="settings-section">
          <h2>${t("settings.llm_config")}</h2>
          <p class="settings-hint">${t("settings.llm_hint")}</p>
          <div class="form-group">
            <label for="api-key-input">${t("settings.api_key_label")}
              <span class="dim" style="margin-left:8px">${t("settings.api_key_current")}：${escapeHtml(masked)}</span>
            </label>
            <input type="password" id="api-key-input" class="input" value="${escapeAttr(apiKey)}" placeholder="sk-..." />
            <p class="dim" style="margin-top:4px">${t("settings.api_key_hint")}</p>
            <button class="btn btn-primary" id="btn-save-key">${t("settings.save_key")}</button>
            <div id="save-key-status" class="save-status"></div>
          </div>
          <div class="form-group">
            <label>${t("settings.daily_usage")}</label>
            <div class="quota-bar">
              <div class="quota-fill" style="width: ${(dailyCount / dailyLimit) * 100}%"></div>
              <span class="quota-text">${dailyCount} / ${dailyLimit} req</span>
            </div>
          </div>
        </section>

        <section class="settings-section">
          <h2>${t("settings.language_title")}</h2>
          <div class="form-group">
            <label>
              <input type="radio" name="lang" value="zh-CN" ${currentLocale === 'zh-CN' ? 'checked' : ''} /> ${t("settings.language_zh")}
            </label>
            <label>
              <input type="radio" name="lang" value="en" ${currentLocale === 'en' ? 'checked' : ''} /> ${t("settings.language_en")}
            </label>
          </div>
        </section>

        <section class="settings-section">
          <h2>${t("settings.engine_tuning")}</h2>
          <details>
            <summary>${t("settings.expand")}</summary>
            <div class="form-group">
              <label>${t("settings.tick_rate")}: <span id="tick-rate-value">30</span> Hz</label>
              <input type="range" id="tick-rate-input" min="10" max="60" step="5" value="30" />
            </div>
            <div class="form-group">
              <label>${t("settings.soft_timeout")}: <span id="soft-timeout-value">50</span> ms</label>
              <input type="range" id="soft-timeout-input" min="20" max="200" step="10" value="50" />
            </div>
            <div class="form-group">
              <label>${t("settings.hard_timeout")}: <span id="hard-timeout-value">200</span> ms</label>
              <input type="range" id="hard-timeout-input" min="100" max="500" step="50" value="200" />
            </div>
          </details>
        </section>

        <section class="settings-section">
          <h2>${t("settings.evals_title")}</h2>
          <p class="settings-hint">${t("settings.evals_hint")}</p>
          <div class="form-group">
            <label>${t("settings.evals_select_level")}</label>
            <select id="evals-project-select" class="input">
              <option value="">${t("settings.evals_select_placeholder")}</option>
            </select>
          </div>
          <div class="form-group">
            <label>${t("settings.evals_running")} ${t("arena.battle")}</label>
            <div class="evals-count-buttons" id="evals-count-buttons">
              <button class="evals-count-btn is-active" data-count="5">5 ${t("arena.battle")}</button>
              <button class="evals-count-btn" data-count="10">10 ${t("arena.battle")}</button>
              <button class="evals-count-btn" data-count="20">20 ${t("arena.battle")}</button>
            </div>
          </div>
          <div class="form-group">
            <button class="btn btn-primary" id="btn-run-evals" disabled>${t("settings.evals_start")}</button>
            <div id="evals-progress" class="evals-progress" hidden>
              <div class="evals-progress-bar-track">
                <div class="evals-progress-bar" id="evals-progress-bar" style="width: 0%"></div>
              </div>
              <span class="evals-progress-text" id="evals-progress-text">${t("settings.evals_running")} 0/5 ${t("arena.battle")}</span>
            </div>
          </div>
          <div id="evals-result" class="evals-result" hidden>
            <h3>${t("settings.evals_result_title")}</h3>
            <div class="evals-metrics" id="evals-metrics"></div>
          </div>
          <div class="form-group" id="evals-history-section">
            <p class="dim">${t("settings.evals_history")}</p>
            <div id="evals-history-list" class="evals-history-list"></div>
          </div>
        </section>

        <section class="settings-section">
          <h2>${t("settings.theme_title")}</h2>
          <div class="form-group">
            <label>
              <input type="radio" name="theme" value="tactical" ${getTheme() === 'tactical' ? 'checked' : ''} /> ${t("settings.theme_tactical")}
            </label>
            <label>
              <input type="radio" name="theme" value="day" ${getTheme() === 'day' ? 'checked' : ''} /> ${t("settings.theme_day")}
            </label>
          </div>
        </section>

        <section class="settings-section">
          <h2>${t("settings.about_title")}</h2>
          <p>${t("settings.about_line1")}</p>
          <p>${t("settings.about_line2")}</p>
          <p class="dim">${t("settings.about_line3")}</p>
        </section>
      </main>
    </div>
  `;

  root.querySelector("#btn-back")?.addEventListener("click", onBack);
  root.querySelector("#btn-save-key")?.addEventListener("click", () => {
    const v = (root.querySelector("#api-key-input") as HTMLInputElement).value;
    localStorage.setItem("battlecode-llm-api-key", v);
    // 内嵌 toast 替代 alert（WebView 不支持 window.alert）
    const status = root.querySelector("#save-key-status") as HTMLElement;
    if (status) {
      status.textContent = t("settings.api_key_saved");
      status.classList.add("toast-success");
      setTimeout(() => {
        status.textContent = "";
        status.classList.remove("toast-success");
      }, 3000);
    }
  });

  // 语言切换绑定
  bindLanguageSwitcher(root);
  // 主题切换
  bindThemeSwitcher(root);

  // 调优滑块
  bindRange(root, "#tick-rate-input", "#tick-rate-value");
  bindRange(root, "#soft-timeout-input", "#soft-timeout-value");
  bindRange(root, "#hard-timeout-input", "#hard-timeout-value");

  // ===== Evals 评估 =====
  initEvalsSection(root);
}

/** 初始化 Evals 区域 */
function initEvalsSection(root: HTMLElement) {
  // 填充关卡下拉列表
  const projectSelect = root.querySelector("#evals-project-select") as HTMLSelectElement | null;
  if (projectSelect) {
    try {
      const raw = localStorage.getItem("battlecode-projects");
      if (raw) {
        const projects = JSON.parse(raw) as Array<{ id: string; name: string; code: string }>;
        for (const p of projects) {
          const opt = document.createElement("option");
          opt.value = p.id;
          opt.textContent = p.name;
          projectSelect.appendChild(opt);
        }
      }
    } catch {
      // ignore
    }
  }

  const runBtn = root.querySelector("#btn-run-evals") as HTMLButtonElement | null;
  const progressDiv = root.querySelector("#evals-progress") as HTMLElement | null;
  const progressBar = root.querySelector("#evals-progress-bar") as HTMLElement | null;
  const progressText = root.querySelector("#evals-progress-text") as HTMLElement | null;
  const resultDiv = root.querySelector("#evals-result") as HTMLElement | null;
  const metricsDiv = root.querySelector("#evals-metrics") as HTMLElement | null;

  // 关卡选择 → 启用按钮
  projectSelect?.addEventListener("change", () => {
    if (runBtn) runBtn.disabled = !projectSelect.value;
  });

  // 局数选择
  let battleCount = 5;
  const countBtns = root.querySelectorAll<HTMLButtonElement>(".evals-count-btn");
  countBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      countBtns.forEach((b) => b.classList.remove("is-active"));
      btn.classList.add("is-active");
      battleCount = parseInt(btn.dataset.count ?? "5", 10);
    });
  });

  // 开始 Evals
  runBtn?.addEventListener("click", async () => {
    if (!projectSelect?.value) return;
    const projectId = projectSelect.value;

    let botCode = "";
    try {
      const raw = localStorage.getItem("battlecode-projects");
      if (raw) {
        const projects = JSON.parse(raw) as Array<{ id: string; name: string; code: string }>;
        const proj = projects.find((p) => p.id === projectId);
        if (proj) botCode = proj.code;
      }
    } catch {
      // ignore
    }

    if (!botCode) {
      alert(t("settings.evals_cant_read"));
      return;
    }

    // 进入加载状态
    if (runBtn) runBtn.disabled = true;
    if (progressDiv) progressDiv.hidden = false;
    if (resultDiv) resultDiv.hidden = true;

    const report = await runEvals(
      botCode,
      {
        battleCount,
        aiOpponents: ["ai_easy_aggressive"],
        mapId: "battle_easy",
      },
      (current, total) => {
        if (progressBar) progressBar.style.width = `${(current / total) * 100}%`;
        if (progressText) progressText.textContent = `${t("settings.evals_running")} ${current}/${total} ${t("arena.battle")}`;
      },
    );

    // 持久化
    saveEvalsReport(report);

    // 显示结果
    if (progressDiv) progressDiv.hidden = true;
    if (resultDiv) resultDiv.hidden = false;
    if (metricsDiv) {
      const m = report.metrics;
      const cq = m.codeQuality;
      metricsDiv.innerHTML = `
        <div class="evals-metrics-grid">
          <div class="evals-metric-card">
            <span class="evals-metric-value">${(m.winRate * 100).toFixed(0)}%</span>
            <span class="evals-metric-label">${t("settings.evals_win_rate")}</span>
          </div>
          <div class="evals-metric-card">
            <span class="evals-metric-value">${m.avgSurvivalTicks.toFixed(0)}</span>
            <span class="evals-metric-label">${t("settings.evals_avg_survival")}</span>
          </div>
          <div class="evals-metric-card">
            <span class="evals-metric-value">${m.avgDamageDealt.toFixed(0)}</span>
            <span class="evals-metric-label">${t("settings.evals_avg_damage")}</span>
          </div>
          <div class="evals-metric-card">
            <span class="evals-metric-value">${cq.hasCommentHeader ? "✓" : "✗"}</span>
            <span class="evals-metric-label">${t("settings.evals_comment_header")}</span>
          </div>
          <div class="evals-metric-card">
            <span class="evals-metric-value">${(cq.blockCommentDensity * 100).toFixed(0)}%</span>
            <span class="evals-metric-label">${t("settings.evals_comment_density")}</span>
          </div>
          <div class="evals-metric-card">
            <span class="evals-metric-value">${cq.apiCallComments}</span>
            <span class="evals-metric-label">${t("settings.evals_api_comments")}</span>
          </div>
        </div>
        <div class="evals-metrics-extra">
          <p>${t("settings.evals_strategy")}: <code>${escapeHtmlStr(m.strategySignature)}</code></p>
          ${cq.forbiddenPatterns.length > 0 ? `<p class="evals-warn">⚠ 检测到禁止模式: ${cq.forbiddenPatterns.join(", ")}</p>` : ""}
        </div>
      `;
    }

    if (runBtn) runBtn.disabled = false;

    // 刷新历史列表
    renderEvalsHistory(root);
  });

  // 历史评估记录
  renderEvalsHistory(root);
}

function renderEvalsHistory(root: HTMLElement) {
  const listEl = root.querySelector("#evals-history-list") as HTMLElement | null;
  if (!listEl) return;

  const history = loadEvalsHistory();
  if (history.length === 0) {
    listEl.innerHTML = `<p class="dim">${t("settings.evals_no_history")}</p>`;
    return;
  }

  listEl.innerHTML = history
    .slice(-5)
    .reverse()
    .map((r) => {
      const d = new Date(r.timestamp);
      const timeStr = `${d.getMonth() + 1}/${d.getDate()} ${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
      return `<div class="evals-history-item">
        <span>${timeStr} · ${r.config.battleCount} 局 · 胜率 ${(r.metrics.winRate * 100).toFixed(0)}% · 注释 ${(r.metrics.codeQuality.blockCommentDensity * 100).toFixed(0)}%</span>
      </div>`;
    })
    .join("");
}

function escapeHtmlStr(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}

function bindRange(root: HTMLElement, inputSel: string, valueSel: string) {
  const input = root.querySelector(inputSel) as HTMLInputElement;
  const value = root.querySelector(valueSel);
  input?.addEventListener("input", () => {
    if (value) value.textContent = input.value;
  });
}

function escapeAttr(s: string): string {
  return s.replace(/"/g, "&quot;");
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]!));
}

/** 语言切换绑定 — 切换后 location.reload() 生效 */
function bindLanguageSwitcher(root: HTMLElement): void {
  const radios = root.querySelectorAll<HTMLInputElement>('input[name="lang"]');
  radios.forEach((radio) => {
    radio.addEventListener("change", () => {
      const value = radio.value as Locale;
      if (value !== getLocale()) {
        setLocale(value);
      }
    });
  });
}

/** 主题切换绑定（FR-029） */
function bindThemeSwitcher(root: HTMLElement): void {
  const radios = root.querySelectorAll<HTMLInputElement>('input[name="theme"]');
  radios.forEach((radio) => {
    radio.addEventListener("change", () => {
      const value = radio.value as "tactical" | "day";
      setTheme(value);
      // 刷新 SVG 缓存（主题色板变化后重建 colored data URL）
      svgLoader.rebuild(BATTLE_SVG_KEYS).catch((e) => {
        console.warn("[settings] svgLoader.rebuild failed:", e);
      });
      // 刷新 color-tokens 缓存（canvas 等模块用）
      refreshColorTokens();
    });
  });
}
